/**
 * Detects whether a markdown response came from Explore mode.
 *
 * The Explore agent has two prompt templates:
 *   - Legacy (Haiku):  "### TL;DR / ### Evidence / ..."  with [PMID:XXX] tokens
 *                      and (Strong)/(Moderate)/(Weak) word grades.
 *   - Deep research:   "## Search Results Summary / ## Key Findings /
 *                       ## Notable Papers / ## Suggested Follow-ups / ## Sources"
 *                      with [PMID:XXX](url) markdown-link citations and
 *                      "Evidence strength: Strong" word grades (or 🟢🟡🟠🔴).
 *
 * A response is treated as Explore when it has BOTH a research-shaped heading
 * AND a research marker (any citation token OR any evidence-grade signal).
 * That keeps plain summaries, write-for-me outputs, and short chat replies
 * out of the mind-map path.
 */

const STRUCTURAL_HEADING =
  /^\s{0,3}#{1,4}\s+(TL;?DR|(Key\s+)?Findings?|Evidence|Search\s+Results|Notable\s+Papers|Suggested\s+Follow|Open\s+Questions?|Next\s+Steps?|Consistency)\b/im;

const GRADE_CHIP = /[\u{1F7E2}\u{1F7E1}\u{1F7E0}\u{1F534}]/u;

const GRADE_WORD =
  /\b(?:Evidence\s+strength|Strength)\s*:?\s*\*{0,2}\s*(?:Strong|Moderate|Weak|High|Low|Very\s+Low)\b/i;

// Tokens may appear bare (PMID:123), inside square brackets ([PMID:123]),
// or as markdown link text that survives AST flattening (PMID:123 with url).
const CITATION_TOKEN =
  /(?:\[\s*)?(?:PMID|DOI|arXiv)\s*:\s*[\w./\-]+/i;

export function isExploreResponse(markdown: string): boolean {
  if (!markdown || markdown.length < 40) return false;
  if (!STRUCTURAL_HEADING.test(markdown)) return false;
  return (
    GRADE_CHIP.test(markdown) ||
    GRADE_WORD.test(markdown) ||
    CITATION_TOKEN.test(markdown)
  );
}
